<template>
  <div class="flex flex-col gap-8 lg:flex-row">
    <!-- Package details -->
    <div class="w-full lg:w-1/2">
      <!-- Page header -->
      <div class="flex items-center gap-3 mb-4">
        <span
          class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500"
        >
          <span class="material-icons-round">card_giftcard</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Edit Reward Package</h2>
          <p class="text-sm text-gray-500">Update this reward package.</p>
        </div>
      </div>

      <form
        @submit.prevent
        class="p-6 space-y-5 bg-white border border-gray-200 shadow-sm rounded-2xl"
      >
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Name</span>
          <input
            v-model="rewardPackage.name"
            class="block w-full mt-1 form-input"
            placeholder="55 Inch TV"
            required
          />
        </label>
        <label class="block">
          <span class="text-sm font-medium text-gray-700">
            Minimum Wallet Balance Required to participate
          </span>
          <input
            v-model="rewardPackage.minimumWalletAmountToQualify"
            class="block w-full mt-1 form-input"
            type="number"
            placeholder="1"
            required
          />
        </label>
        <div class="flex items-center gap-3 pt-2">
          <back-button></back-button>
          <button
            class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
            @click="updatePackage"
          >
            <span class="mr-1 text-base material-icons-round">save</span>
            Save Changes
          </button>
        </div>
      </form>
    </div>

    <!-- Prizes -->
    <div class="flex flex-col w-full gap-3 lg:w-1/2">
      <div class="flex items-center space-x-1 text-gray-500">
        <span class="text-base material-icons-round">emoji_events</span>
        <span class="text-xs font-semibold tracking-widest uppercase">Prizes</span>
      </div>

      <div
        class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Prize',
                    'Initial Stock',
                    'In Stock',
                    'Created',
                    '',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="rewardPackage.prizes.length">
              <tr
                v-for="(prize, index) in rewardPackage.prizes"
                :key="prize._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex-shrink-0 w-10 h-10 bg-center bg-cover border border-gray-200 rounded-full bg-taxiye-50"
                      :style="{ backgroundImage: `url('${prize.prizeImage}')` }"
                    ></span>
                    <p class="font-medium text-gray-900">{{ prize.prizeItem }}</p>
                  </div>
                </td>
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ prize.initialAmountInStock }}
                </td>
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-md text-taxiye-700 bg-taxiye-50"
                  >
                    {{ prize.amountInStock }}
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ formatDate(prize.createdAt) }}
                </td>
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <button
                    @click="removePrize(prize)"
                    type="button"
                    title="Remove prize"
                    class="inline-flex items-center justify-center text-red-600 transition-colors duration-150 rounded-lg w-9 h-9 hover:bg-red-50 focus:outline-none"
                  >
                    <i class="text-base material-icons-round">delete</i>
                  </button>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span
                      class="flex items-center justify-center w-16 h-16 mb-3 bg-gray-100 rounded-full"
                    >
                      <i class="text-3xl material-icons-round">emoji_events</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No prizes added yet</span>
                    <span class="mt-1 text-xs text-gray-400">Add a prize to this package</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Add prize -->
      <div class="relative">
        <button
          class="inline-flex items-center justify-center w-full h-10 px-4 text-sm font-semibold transition-colors duration-150 border rounded-xl text-taxiye-600 border-taxiye-200 bg-taxiye-50 hover:bg-taxiye-100"
          @click="togglePrizes()"
        >
          <span class="mr-1 text-base material-icons-round">add</span>
          Add New Prize
        </button>
        <div
          class="absolute left-0 z-50 w-full p-4 mt-2 bg-white border border-gray-200 shadow-lg rounded-xl top-full"
          v-if="addingPrize"
        >
          <div
            class="flex items-center justify-center py-8 text-sm text-gray-500"
            v-if="loadingPrizes"
          >
            <i class="mr-2 text-2xl text-taxiye-500 material-icons-round spin">sync</i>
            Loading prizes...
          </div>
          <div
            class="flex gap-4 pb-2 overflow-x-auto"
            v-else-if="availablePrizes && availablePrizes.length"
          >
            <div
              class="relative flex items-end justify-start flex-shrink-0 w-40 h-56 overflow-hidden text-white transition-shadow duration-150 bg-center bg-cover bg-gray-300 cursor-pointer rounded-xl hover:shadow-lg"
              :style="{ backgroundImage: `url('${prize.prizeImage}')` }"
              v-for="prize in availablePrizes"
              :key="prize._id"
              @click="addPrize(prize)"
            >
              <div
                class="absolute inset-x-0 bottom-0 p-3 text-sm font-semibold bg-black bg-opacity-50"
              >
                {{ prize.prizeItem }}
              </div>
            </div>
          </div>
          <div class="py-8 text-sm text-center text-gray-400" v-else>
            No prizes found
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BackButton from "@/components/BackButton.vue";

export default {
  components: {
    BackButton,
  },
  data() {
    return {
      rewardPackage: {
        name: null,
        minimumWalletAmountToQualify: null,
        prizes: [],
      },
      loading: false,
      addingPrize: false,
      availablePrizes: [],
      loadingPrizes: true,
    };
  },
  created() {
    this.getPackage();
  },
  methods: {
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    getPackage() {
      this.$misc
        .get(`reward-packages/${this.$route.params.id}`)
        .then((response) => {
          this.rewardPackage = response.data;
        })
        .catch((err) => console.log(err));
    },
    addPrize(prize) {
      this.rewardPackage.prizes = [
        ...this.rewardPackage.prizes.filter((x) => x._id !== prize._id),
        prize,
      ];
      this.addingPrize = false;
    },
    removePrize(prize) {
      this.rewardPackage.prizes = this.rewardPackage.prizes.filter(
        (x) => x._id !== prize._id
      );
    },
    togglePrizes() {
      this.addingPrize = !this.addingPrize;
      this.$misc
        .get(`rewards-inventory`)
        .then((response) => {
          this.availablePrizes = response.data.data;
          this.loadingPrizes = false;
        })
        .catch((err) => console.log(err));
    },
    updatePackage() {
      this.$misc
        .patch(`reward-packages/${this.$route.params.id}`, {
          ...this.rewardPackage,
          prizes: this.rewardPackage.prizes.map(({ _id }) => _id),
        })
        .then(() => {
          window.flash({
            type: "success",
            body: "Prize updated successfully!",
          });
          this.$router.push("/reward-packages");
        })
        .catch((err) => {
          window.flash({
            type: "error",
            body: "Failed to update the prize!",
          });
          console.log(err);
        });
    },
  },
};
</script>
